// v3.0.0-rc.8 — PadHalo (ex-LiveBanner) : cadre UTILITAIRE permanent autour du pad chat.
// Rupture design 2026-07-05 :
//  - Plus AUCUNE campagne pub, plus AUCUN fetch backend, plus AUCUNE croix dismiss.
//  - Le halo abrite désormais : masque AYA (menu, à gauche) + panier + Mon compte (à droite).
//  - Coins supérieurs CARRÉS (0px), coins inférieurs arrondis 22px (style "onglet posé sur le bord").
//  - Toujours visible.
// L'ancienne LiveBanner (fetch /api/v3/live-banner/current, gradient chaud, badge sparkle,
//   pill NEW, croix dismiss) est archivée dans PRD/CHANGELOG.

import { useCallback, useEffect, useRef, useState } from "react";
import CartDrawer from "./CartDrawer";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Bell, BellRing, Video, Newspaper, User, Globe } from "lucide-react";
import KiosqueStrip from "@/components/content/KiosqueStrip";
import { cartCount as localCartCount, subscribe as subLocalCart } from "@/lib/localCartStore";
import { pushDepleted, pushMerchantOnline, markOnlineSeen, getState as getStreamState, subscribe as subStream } from "@/lib/cartStreamStore";
import { playPresenceChimeIfFirstOfDay } from "@/lib/presenceChime";
import { useAuth } from "@/hooks/useAuth";
// v3.0.0-rc.8z-notif-install — Notif d'installation PWA (owner + tous les users).
import { isStandalone, isIOS, isInstallPromptAvailable, triggerInstallPrompt } from "@/pwa/registerSW";

const AYA_MASK = "/aya/notch.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const CART_API = `${BACKEND_URL}/api`;

// v3.0.0-rc.8r — Helpers pour la panel Notifications.
function _iconForKind(k) {
  const s = String(k || "").toLowerCase();
  if (s.includes("avocat") || s.includes("juri") || s.includes("legal")) return "⚖️";
  if (s.includes("med") || s.includes("doct") || s.includes("sante")) return "🩺";
  if (s.includes("coif") || s.includes("hair") || s.includes("tres")) return "💇";
  if (s.includes("plomb") || s.includes("elec") || s.includes("meca")) return "🔧";
  if (s.includes("resto") || s.includes("food") || s.includes("maquis")) return "🍽";
  if (s.includes("beaut") || s.includes("spa")) return "💅";
  if (s.includes("dispatch") || s.includes("livr") || s.includes("delivery")) return "🛵";
  return "📅";
}
function _fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const dayMs = 86400000;
    const diff = d.getTime() - now.getTime();
    const abs = Math.abs(diff);
    const dateFmt = new Intl.DateTimeFormat("fr-FR", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    if (abs < dayMs) {
      return diff < 0 ? `Il y a ${Math.max(1, Math.round(abs / 3600000))}h` : `Dans ${Math.max(1, Math.round(diff / 3600000))}h · ${dateFmt.format(d)}`;
    }
    return dateFmt.format(d);
  } catch (_) { return ""; }
}

// v3.0.0-rc.8e — Bascule SSE → le polling 45s devient un simple filet de sécurité
// (5 min) au cas où la connexion SSE tomberait sans reconnexion.
const POLL_INTERVAL_MS = 5 * 60_000;

// Hash simple pour détecter une évolution du panier (nouvelle commande OU changement de statut).
function cartFingerprint(orders, bookings) {
  const o = (orders || []).map((x) => `${x.order_ref}:${x.status}`).sort().join("|");
  const b = (bookings || []).map((x) => `${x.booking_id}:${x.status}`).sort().join("|");
  return `o=${o}#b=${b}`;
}

function seenKey() {
  const phone = (typeof window !== "undefined" && (localStorage.getItem("civ_user_phone") || "")) || "anon";
  return `aya_cart_seen_${phone.replace(/\W/g, "")}`;
}

export default function LiveBanner({ children, onMenuClick, thinking = false, minimal = false }) {
  const [cartOpen, setCartOpen] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [localCount, setLocalCount] = useState(() => localCartCount());
  const [hasNews, setHasNews] = useState(false);
  const lastFingerprintRef = useRef("");
  const navigate = useNavigate();
  // v3.18.0 — Compte diaspora → raccourci 🌍 Espace Diaspora dans le halo.
  const { user: _authUser } = useAuth();
  const _isDiaspora = _authUser?.user_type === "diaspora";
  // v3.0.0-rc.8w — AYA Live discovery drawer (Halo → lives actifs).
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveSessions, setLiveSessions] = useState([]);
  const [liveLoading, setLiveLoading] = useState(false);
  // v3.6.1 — Kiosque : panneau popover ouvert via l'icône News du Halo.
  const [newsOpen, setNewsOpen] = useState(false);

  const fetchLive = useCallback(async () => {
    setLiveLoading(true);
    // v3.0.0-rc.8z-pwa — Signal PWA install (live discovery ouvert).
    try { require('@/pwa/valueSignals').trackSignal('live_discovery_opened'); } catch (_) {}
    try {
      const r = await fetch(`${CART_API}/v3/live/sessions/active`);
      const b = await r.json();
      setLiveSessions(b.sessions || []);
    } catch (_) { /* silent */ }
    finally { setLiveLoading(false); }
  }, []);

  // Total affiché sur le badge = orders + bookings + items locaux.
  const totalBadgeCount = activeCount + localCount;

  // Souscription aux mutations du panier local (dispatchées par localCartStore).
  useEffect(() => {
    setLocalCount(localCartCount());
    const off = subLocalCart((items) => setLocalCount(items.reduce((s, x) => s + (x.qty || 0), 0)));
    return () => { try { off && off(); } catch (_) { /* noop */ } };
  }, []);

  const fetchCart = useCallback(async () => {
    const token = (typeof window !== "undefined" && localStorage.getItem("civ_user_token")) || "";
    if (!token) {
      setActiveCount(0);
      setHasNews(false);
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [oRes, bRes] = await Promise.all([
        fetch(`${CART_API}/v3/delivery/my-client-orders?active=true`, { headers }).catch(() => null),
        fetch(`${CART_API}/v3/me/appointments`, { headers }).catch(() => null),
      ]);
      const orders = oRes && oRes.ok ? (await oRes.json()).orders || [] : [];
      const bookings = bRes && bRes.ok ? (await bRes.json()).appointments || [] : [];
      const count = orders.length + bookings.length;
      setActiveCount(count);
      const fp = cartFingerprint(orders, bookings);
      lastFingerprintRef.current = fp;
      let lastSeen = "";
      try { lastSeen = localStorage.getItem(seenKey()) || ""; } catch (_) { /* noop */ }
      setHasNews(fp !== lastSeen && count > 0);
    } catch (_) { /* silent — pas de dot si erreur réseau */ }
  }, []);

  useEffect(() => {
    fetchCart();
    const id = setInterval(fetchCart, POLL_INTERVAL_MS);
    // Rafraîchir aussi quand la fenêtre revient au premier plan.
    const onVis = () => { if (document.visibilityState === "visible") fetchCart(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [fetchCart]);

  // v3.0.0-rc.8e — Cart Realtime Push : EventSource sur /me/cart-stream.
  // Chaque event `cart-update` déclenche un re-fetch immédiat. Le polling 5min
  // reste comme filet de sécurité si la connexion SSE tombe.
  useEffect(() => {
    const token = (typeof window !== "undefined" && localStorage.getItem("civ_user_token")) || "";
    if (!token) return undefined;
    let es = null;
    let cancelled = false;
    try {
      const url = `${CART_API}/v3/me/cart-stream?token=${encodeURIComponent(token)}`;
      es = new EventSource(url);
      es.addEventListener("hello", () => { if (!cancelled) fetchCart(); });
      es.addEventListener("cart-update", (ev) => {
        if (cancelled) return;
        // v3.0.0-rc.8f — Route les sous-événements vers le cartStreamStore.
        let data = null;
        try { data = ev?.data ? JSON.parse(ev.data) : null; } catch (_) { /* noop */ }
        if (data?.kind === "stock_depleted" && data.product_id) {
          pushDepleted(data.product_id);
        } else if (data?.kind === "merchant_online" && data.merchant_id) {
          pushMerchantOnline({ merchant_id: data.merchant_id, name: data.name });
          // v3.0.0-rc.8h — Chime doux (1 fois par jour) à la première ouverture.
          playPresenceChimeIfFirstOfDay();
        }
        // Fetch panier uniquement pour événements orders/appointments/dispatch/delivery.
        if (!data?.kind || ["dispatch", "delivery", "appointment"].includes(data.kind)) {
          fetchCart();
        }
      });
      es.onerror = () => {
        // Laisse EventSource retenter automatiquement (retry: 3s par défaut).
      };
    } catch (_) { /* SSE non supporté → polling seul */ }
    return () => { cancelled = true; try { es && es.close(); } catch (_) { /* noop */ } };
  }, [fetchCart]);

  // v3.0.0-rc.8f — Bell de présence marchand : nombre de favoris récemment en ligne.
  const [unseenOnline, setUnseenOnline] = useState(0);
  const [latestOnlineName, setLatestOnlineName] = useState(null);
  useEffect(() => {
    const apply = (s) => {
      setUnseenOnline(s.unseenOnline || 0);
      const latest = s.online?.[s.online.length - 1];
      setLatestOnlineName(latest?.name || null);
    };
    apply(getStreamState());
    const off = subStream(apply);
    return () => { try { off && off(); } catch (_) { /* noop */ } };
  }, []);

  // v3.0.0-rc.8r — Notifications (rappels RDV coiffeur, avocat, médecin, plombier…).
  // Le LLM y alimente les rappels via /api/v3/me/appointments (upcoming). Le badge
  // affiche le nombre de RDV dans les 24h à venir. Clic → ouvre un dropdown listant.
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const fetchNotifs = useCallback(async () => {
    const token = typeof window !== "undefined" ? localStorage.getItem("civ_user_token") : "";
    if (!token) return;
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
      const r = await fetch(`${API}/v3/me/appointments?upcoming=true&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const d = await r.json();
      setNotifs(Array.isArray(d.appointments) ? d.appointments : []);
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => {
    fetchNotifs();
    const iv = setInterval(fetchNotifs, 60_000); // refresh every 60s
    return () => clearInterval(iv);
  }, [fetchNotifs]);
  // v3.8.0 — Notifications livraison (cloche Halo) : badge non-vues + liste.
  const [deliveryNotifs, setDeliveryNotifs] = useState([]);
  const [deliveryUnseen, setDeliveryUnseen] = useState(0);
  const fetchBell = useCallback(async () => {
    const token = typeof window !== "undefined" ? localStorage.getItem("civ_user_token") : "";
    if (!token) return;
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
      const r = await fetch(`${API}/v3/notifications/bell`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      if (d.ok) {
        setDeliveryNotifs(Array.isArray(d.notifications) ? d.notifications : []);
        setDeliveryUnseen(Number(d.unseen_count) || 0);
      }
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => {
    fetchBell();
    const iv = setInterval(fetchBell, 15_000);
    return () => clearInterval(iv);
  }, [fetchBell]);
  const ackBell = useCallback(async () => {
    const token = typeof window !== "undefined" ? localStorage.getItem("civ_user_token") : "";
    if (!token || deliveryUnseen === 0) return;
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
      await fetch(`${API}/v3/notifications/bell/ack`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      setDeliveryUnseen(0);
    } catch (_) { /* silent */ }
  }, [deliveryUnseen]);

  // v3.0.0-rc.8z-notif-install — Notif "Installer AYA" visible tant que non installée + non dismissée.
  const [installNotifVisible, setInstallNotifVisible] = useState(false);
  const [installBusy, setInstallBusy] = useState(false);
  useEffect(() => {
    const evaluate = () => {
      try {
        if (isStandalone()) { setInstallNotifVisible(false); return; }
        const dismissedAt = Number(localStorage.getItem('aya_install_notif_dismissed_at') || 0);
        if (dismissedAt && (Date.now() - dismissedAt) < 7 * 24 * 60 * 60 * 1000) {
          setInstallNotifVisible(false); return;
        }
        setInstallNotifVisible(true);
      } catch (_) { setInstallNotifVisible(true); }
    };
    evaluate();
    const onFocus = () => evaluate();
    window.addEventListener('focus', onFocus);
    window.addEventListener('appinstalled', onFocus);
    return () => { window.removeEventListener('focus', onFocus); window.removeEventListener('appinstalled', onFocus); };
  }, []);
  const dismissInstallNotif = useCallback(() => {
    try { localStorage.setItem('aya_install_notif_dismissed_at', String(Date.now())); } catch (_) { /* silent */ }
    setInstallNotifVisible(false);
  }, []);
  const handleInstallNotifClick = useCallback(async () => {
    if (installBusy) return;
    if (isIOS() && !isInstallPromptAvailable()) {
      // Ouvre un mini-tuto iOS via alert (fallback minimal — le composant InstallPrompt gère mieux)
      try { alert("Sur iPhone/iPad :\n1. Appuie sur l'icône Partager ⎗ en bas de Safari\n2. Choisis « Sur l'écran d'accueil »\n\nAYA s'ouvrira comme une vraie app."); } catch (_) { /* silent */ }
      return;
    }
    if (!isInstallPromptAvailable()) {
      try { alert("Le navigateur n'a pas encore proposé l'installation. Continue à utiliser AYA — l'invitation apparaîtra automatiquement dès que tu auras plus d'interactions."); } catch (_) { /* silent */ }
      return;
    }
    setInstallBusy(true);
    const res = await triggerInstallPrompt();
    setInstallBusy(false);
    if (res.outcome === 'accepted') setInstallNotifVisible(false);
  }, [installBusy]);
  const notifCount = notifs.length + deliveryUnseen + (installNotifVisible ? 1 : 0);

  const handleMenu = useCallback(() => {
    onMenuClick && onMenuClick();
  }, [onMenuClick]);

  const handleCart = useCallback(() => {
    setCartOpen(true);
    // Marque tout comme vu → efface le dot immédiatement (attente utilisateur).
    try { localStorage.setItem(seenKey(), lastFingerprintRef.current); } catch (_) { /* noop */ }
    setHasNews(false);
  }, []);
  const handleAccount = useCallback(() => {
    try { navigate("/mon-compte"); }
    catch (_) { window.location.href = "/mon-compte"; }
  }, [navigate]);

  return (
    <div
      data-testid="pad-halo-root"
      className={`aya-livebanner-frame${thinking ? " aya-livebanner-thinking" : ""}`}
      style={{
        // v3.0.0-rc.8m — Halo encore plus compact : padding vertical réduit
        // (top 3→1, bottom 8→4), header minHeight 28→22, marges latérales
        // desktop 12px (aligné sur HomeCard) pour ne plus étouffer le contenu.
        // v3.0.0-rc.8x — Vert émeraude en dark mode (var CSS), fallback noir sinon.
        borderRadius: 16,
        background: "var(--civ-emerald-deep, #0A0A0C)",
        padding: "1px 10px 4px 10px",
        boxShadow: "0 0 0 1px rgba(255,255,255,0.14), 0 0 28px rgba(16,185,129,0.10), 0 10px 30px rgba(0,0,0,0.45)",
        boxSizing: "border-box",
      }}
    >
      {/* Header utilitaire : menu (gauche) + cart + mon compte (droite) */}
      <div
        className="aya-pad-halo-header"
        style={{
          display: minimal ? "none" : "flex",
          alignItems: "center",
          gap: 4,
          padding: "0px 2px 1px 2px",
          minHeight: 22,
        }}
      >
        {/* Masque menu — abrité sur le rond gauche */}
        <button
          type="button"
          data-testid="pad-halo-menu"
          onClick={handleMenu}
          aria-label="Ouvrir le menu"
          className="aya-pad-halo-mask-btn"
          style={{
            width: 54, height: 54,
            padding: 0, border: "none",
            background: "transparent",
            borderRadius: "50%",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
            transition: "transform 180ms ease",
          }}
        >
          <img
            src={AYA_MASK}
            alt=""
            style={{
              width: 52, height: 52,
              objectFit: "contain",
              filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.35))",
              pointerEvents: "none",
            }}
          />
        </button>

        {/* Spacer / brand discret */}
        <div style={{
          flex: 1, minWidth: 0,
          fontSize: 11, fontWeight: 700,
          letterSpacing: 0.5, textTransform: "uppercase",
          color: "rgba(244,241,232,0.42)",
          paddingLeft: 4,
        }}>
          AYA
        </div>

        {/* v3.0.0-rc.8f — Bell de présence : bipe si un favori vient de passer en ligne. */}
        {unseenOnline > 0 && (
          <button
            type="button"
            data-testid="pad-halo-presence-bell"
            title={latestOnlineName ? `${latestOnlineName} vient d'ouvrir` : "Un commerçant favori est en ligne"}
            onClick={() => {
              markOnlineSeen();
              try { navigate("/mon-compte?tab=favoris"); }
              catch (_) { window.location.href = "/mon-compte?tab=favoris"; }
            }}
            className="aya-pad-halo-icon-btn aya-presence-pulse"
            style={{
              position: "relative",
              width: 28, height: 28, padding: 0,
              border: "1px solid rgba(255,126,27,0.55)",
              background: "linear-gradient(135deg, rgba(255,126,27,0.16), rgba(245,158,11,0.10))",
              borderRadius: 10,
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 15, flexShrink: 0,
            }}
          >
            <BellRing size={14} strokeWidth={2.2} aria-hidden />
            <span
              aria-hidden
              style={{
                position: "absolute", top: -4, right: -4,
                minWidth: 15, height: 15, padding: "0 4px",
                borderRadius: 8,
                background: "#FF7E1B", color: "#fff",
                fontSize: 9.5, fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "2px solid #0A0A0C",
                lineHeight: 1,
              }}
            >{unseenOnline > 9 ? "9+" : unseenOnline}</span>
          </button>
        )}

        {/* Panier — ouvre le drawer, ne quitte pas le chat */}
        <button
          type="button"
          data-testid="pad-halo-cart"
          onClick={handleCart}
          aria-label={
            hasNews
              ? `Ouvrir le panier — nouvelle activité (${totalBadgeCount})`
              : (totalBadgeCount > 0 ? `Ouvrir le panier — ${totalBadgeCount} en cours` : "Ouvrir le panier")
          }
          className="aya-pad-halo-icon-btn"
          style={{
            position: "relative",
            width: 28, height: 28, padding: 0,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.06)",
            color: "#f4f1e8",
            borderRadius: 10,
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 15,
            flexShrink: 0,
            transition: "transform 150ms ease, background 150ms ease",
          }}
        >
          <ShoppingCart size={14} strokeWidth={2.2} aria-hidden />
          {totalBadgeCount > 0 && (
            <span
              data-testid="pad-halo-cart-count"
              aria-hidden
              style={{
                position: "absolute",
                top: -4, right: -4,
                minWidth: 15, height: 15,
                padding: "0 4px",
                borderRadius: 8,
                background: hasNews ? "#EF4444" : "#F4F1E8",
                color: hasNews ? "#fff" : "#0F172A",
                fontSize: 9.5,
                fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
                border: "2px solid #0A0A0C",
                lineHeight: 1,
              }}
            >{totalBadgeCount > 9 ? "9+" : totalBadgeCount}</span>
          )}
          {totalBadgeCount === 0 && hasNews && (
            <span
              data-testid="pad-halo-cart-dot"
              aria-hidden
              className="aya-cart-pulse-dot"
              style={{
                position: "absolute",
                top: 2, right: 2,
                width: 8, height: 8,
                borderRadius: "50%",
                background: "#EF4444",
                boxShadow: "0 0 0 2px #0A0A0C",
              }}
            />
          )}
        </button>

        {/* v3.0.0-rc.8w — AYA Live discovery : découvre les lives commerçants actifs sans quitter AYA. */}
        <button
          type="button"
          data-testid="pad-halo-live"
          onClick={() => { setLiveOpen((v) => !v); fetchLive(); }}
          aria-label={liveSessions.length > 0 ? `${liveSessions.length} live${liveSessions.length > 1 ? "s" : ""} en cours` : "Découvrir les lives"}
          className="aya-pad-halo-icon-btn"
          style={{
            position: "relative",
            width: 28, height: 28, padding: 0,
            border: "1px solid rgba(239,68,68,0.35)",
            background: liveOpen ? "rgba(239,68,68,0.22)" : "rgba(239,68,68,0.10)",
            color: "#f4f1e8",
            borderRadius: 10,
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 15,
            flexShrink: 0,
          }}
        >
          <Video size={14} strokeWidth={2.2} aria-hidden />
          {liveSessions.length > 0 && (
            <span
              data-testid="pad-halo-live-count"
              aria-hidden
              className="aya-cart-pulse-dot"
              style={{
                position: "absolute",
                top: -4, right: -4,
                minWidth: 15, height: 15,
                padding: "0 4px",
                borderRadius: 8,
                background: "#EF4444",
                color: "#fff",
                fontSize: 9.5,
                fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "2px solid #0A0A0C",
                lineHeight: 1,
              }}
            >{liveSessions.length > 9 ? "9+" : liveSessions.length}</span>
          )}
        </button>

        {/* v3.6.1 — Kiosque News : les journaux du jour, abrités dans le Halo. */}
        <button
          type="button"
          data-testid="pad-halo-news"
          onClick={() => setNewsOpen((v) => !v)}
          aria-label="Ouvrir le kiosque journaux"
          className="aya-pad-halo-icon-btn"
          style={{
            position: "relative",
            width: 28, height: 28, padding: 0,
            border: "1px solid rgba(16,185,129,0.35)",
            background: newsOpen ? "rgba(16,185,129,0.20)" : "rgba(16,185,129,0.08)",
            color: "#f4f1e8",
            borderRadius: 10,
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 15,
            flexShrink: 0,
          }}
        >
          <Newspaper size={14} strokeWidth={2.2} aria-hidden />
        </button>

        {/* v3.0.0-rc.8r — Notifications : rappels RDV (avocat/médecin/coiffeur/plombier…). */}
        <button
          type="button"
          data-testid="pad-halo-notifications"
          onClick={() => { setNotifOpen((v) => !v); fetchNotifs(); fetchBell(); ackBell(); }}
          aria-label={notifCount > 0 ? `${notifCount} rappel${notifCount > 1 ? "s" : ""}` : "Notifications"}
          className="aya-pad-halo-icon-btn"
          style={{
            position: "relative",
            width: 28, height: 28, padding: 0,
            border: "1px solid rgba(255,255,255,0.12)",
            background: notifOpen ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.06)",
            color: "#f4f1e8",
            borderRadius: 10,
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 15,
            flexShrink: 0,
          }}
        >
          <Bell size={14} strokeWidth={2.2} aria-hidden />
          {notifCount > 0 && (
            <span
              data-testid="pad-halo-notif-count"
              aria-hidden
              style={{
                position: "absolute",
                top: -4, right: -4,
                minWidth: 15, height: 15,
                padding: "0 4px",
                borderRadius: 8,
                background: "#FF7E1B",
                color: "#fff",
                fontSize: 9.5,
                fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "2px solid #0A0A0C",
                lineHeight: 1,
              }}
            >{notifCount > 9 ? "9+" : notifCount}</span>
          )}
        </button>

        {/* v3.18.0 — Espace Diaspora (visible pour les comptes diaspora). */}
        {_isDiaspora && (
          <button
            type="button"
            data-testid="pad-halo-diaspora"
            onClick={() => { try { navigate("/diaspora"); } catch (_) { window.location.href = "/diaspora"; } }}
            aria-label="Ouvrir mon Espace Diaspora"
            className="aya-pad-halo-icon-btn"
            style={{
              width: 28, height: 28, padding: 0,
              border: "1px solid rgba(239,199,94,0.45)",
              background: "rgba(239,199,94,0.12)",
              color: "#EFC75E",
              borderRadius: 10,
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 15,
              flexShrink: 0,
              transition: "transform 150ms ease, background 150ms ease",
            }}
          ><Globe size={14} strokeWidth={2.2} aria-hidden /></button>
        )}

        {/* Mon compte — quitte le chat */}
        <button
          type="button"
          data-testid="pad-halo-account"
          onClick={handleAccount}
          aria-label="Aller à mon compte"
          className="aya-pad-halo-icon-btn"
          style={{
            width: 28, height: 28, padding: 0,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.06)",
            color: "#f4f1e8",
            borderRadius: 10,
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 15,
            flexShrink: 0,
            transition: "transform 150ms ease, background 150ms ease",
          }}
        ><User size={14} strokeWidth={2.2} aria-hidden /></button>
      </div>

      {/* Pad chat enclavé — enfants = footer civ-input-bar */}
      <div
        className="aya-livebanner-pad-wrapper"
        style={{
          borderRadius: 16,
          overflow: "hidden",
        }}
      >
        {children}
      </div>

      {/* v3.0.0-rc.8r — Dropdown Notifications (RDV à venir). */}
      {notifOpen && (
        <div
          data-testid="pad-halo-notif-panel"
          role="dialog"
          aria-label="Notifications"
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            right: 10,
            width: "min(320px, calc(100vw - 24px))",
            maxHeight: 360,
            overflowY: "auto",
            background: "#0F0F12",
            border: "1px solid rgba(255,255,255,0.14)",
            borderRadius: 14,
            boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
            zIndex: 60,
            padding: 12,
            color: "#F4F1E8",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 }}>
              🔔 Rappels {notifCount > 0 ? `(${notifCount})` : ""}
            </div>
            <button
              data-testid="pad-halo-notif-close"
              onClick={() => setNotifOpen(false)}
              aria-label="Fermer"
              style={{
                background: "transparent", border: "none", color: "inherit",
                cursor: "pointer", fontSize: 18, lineHeight: 1,
              }}
            >×</button>
          </div>
          {notifs.length === 0 && deliveryNotifs.length === 0 && !installNotifVisible ? (
            <div style={{ padding: "18px 8px", textAlign: "center", opacity: 0.6, fontSize: 12.5, lineHeight: 1.5 }}>
              Aucun rappel pour le moment.<br />
              <span style={{ opacity: 0.5 }}>Les prochains RDV (coiffeur, médecin, avocat…) apparaîtront ici.</span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {installNotifVisible && (
                <div
                  data-testid="pad-halo-notif-install-aya"
                  style={{
                    textAlign: "left",
                    background: "linear-gradient(135deg, rgba(11,69,49,0.6), rgba(3,33,23,0.6))",
                    border: "1px solid rgba(232,213,163,0.4)",
                    borderRadius: 10,
                    padding: "10px 12px",
                    color: "inherit",
                    display: "flex", alignItems: "center", gap: 10,
                    position: "relative",
                  }}
                >
                  <img src="/pwa/icon-192.png" alt="" width="40" height="40" style={{ borderRadius: 8, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800 }}>Installer AYA sur ton téléphone</div>
                    <div style={{ fontSize: 10.5, opacity: 0.72, lineHeight: 1.35, marginTop: 2 }}>Accès instantané, sans navigateur — comme une vraie app.</div>
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      <button
                        data-testid="pad-halo-notif-install-cta"
                        onClick={handleInstallNotifClick}
                        disabled={installBusy}
                        style={{
                          padding: "5px 12px", borderRadius: 999, border: "none", cursor: "pointer",
                          background: "linear-gradient(135deg, #F5E9C6, #E4C87F 55%, #CDA452)",
                          color: "#3A2E10", fontSize: 11, fontWeight: 800,
                          opacity: installBusy ? 0.6 : 1,
                        }}
                      >{installBusy ? "…" : "Installer"}</button>
                      <button
                        data-testid="pad-halo-notif-install-dismiss"
                        onClick={dismissInstallNotif}
                        style={{
                          padding: "5px 10px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.14)",
                          background: "transparent", color: "rgba(244,241,232,0.75)", fontSize: 11, cursor: "pointer",
                        }}
                      >Plus tard</button>
                    </div>
                  </div>
                </div>
              )}
              {/* v3.8.0 — Notifications livraison (commandes, livreur, livrée…). */}
              {deliveryNotifs.length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.55, margin: "4px 2px 0" }}>
                    📦 Livraisons
                  </div>
                  {deliveryNotifs.map((n, i) => (
                    <div
                      key={n.id || `dlv-${i}`}
                      data-testid={`pad-halo-delivery-notif-${i}`}
                      style={{
                        textAlign: "left",
                        background: n.unseen ? "rgba(11,69,49,0.55)" : "rgba(255,255,255,0.04)",
                        border: n.unseen ? "1px solid rgba(0,255,136,0.35)" : "1px solid rgba(255,255,255,0.08)",
                        borderRadius: 10,
                        padding: "9px 11px",
                        fontSize: 11.5,
                        lineHeight: 1.45,
                        color: "inherit",
                        position: "relative",
                      }}
                    >
                      {n.unseen && <span style={{ position: "absolute", top: 8, right: 8, width: 7, height: 7, borderRadius: "50%", background: "#00FF88", boxShadow: "0 0 8px rgba(0,255,136,0.8)" }} />}
                      <span style={{ paddingRight: 14, display: "block" }}>{String(n.text || "").replace(/\*\*/g, "").replace(/`/g, "")}</span>
                      {n.kind === "beaute_reminder" && n.meta?.booking_id && (
                        <div style={{ display: "flex", gap: 7, marginTop: 7 }}>
                          <button
                            data-testid={`notif-rdv-cancel-${i}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              const token = localStorage.getItem("civ_user_token");
                              try {
                                const API2 = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
                                const r = await fetch(`${API2}/v3/beaute/bookings/${n.meta.booking_id}/cancel`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
                                const d = await r.json();
                                if (d.ok) { setDeliveryNotifs((arr) => arr.map((x) => (x === n ? { ...x, text: "✔️ Rendez-vous annulé." , meta: {} } : x))); }
                              } catch (_) { /* silent */ }
                            }}
                            style={{ flex: 1, padding: "7px 8px", borderRadius: 8, border: "1px solid rgba(239,68,68,0.45)", background: "rgba(239,68,68,0.1)", color: "#F87171", fontWeight: 800, fontSize: 10.5, cursor: "pointer" }}
                          >✕ Annuler le RDV</button>
                          <button
                            data-testid={`notif-rdv-move-${i}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const token = localStorage.getItem("civ_user_token");
                              const API2 = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
                              // Déplacer = annule l'ancien créneau puis rouvre la réservation du salon.
                              fetch(`${API2}/v3/beaute/bookings/${n.meta.booking_id}/cancel`, { method: "POST", headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
                              setNotifOpen(false);
                              const sid = n.meta.salon_id;
                              try { navigate(`/place/${sid}`); } catch (_) { window.location.href = `/place/${sid}`; }
                            }}
                            style={{ flex: 1, padding: "7px 8px", borderRadius: 8, border: "1px solid rgba(0,255,136,0.4)", background: "rgba(0,255,136,0.08)", color: "#00FF88", fontWeight: 800, fontSize: 10.5, cursor: "pointer" }}
                          >📅 Déplacer</button>
                        </div>
                      )}
                      <span style={{ fontSize: 9.5, opacity: 0.5, display: "block", marginTop: 3 }}>
                        {n.created_at ? new Date(n.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : ""}
                      </span>
                    </div>
                  ))}
                </>
              )}
              {notifs.map((n, i) => (
                <button
                  key={n.id || `notif-${i}`}
                  data-testid={`pad-halo-notif-item-${i}`}
                  onClick={() => {
                    setNotifOpen(false);
                    try { navigate("/mon-compte?tab=rdv"); }
                    catch (_) { window.location.href = "/mon-compte?tab=rdv"; }
                  }}
                  style={{
                    textAlign: "left",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 10,
                    padding: "10px 12px",
                    color: "inherit",
                    cursor: "pointer",
                    display: "flex", flexDirection: "column", gap: 3,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    <span aria-hidden style={{ marginRight: 6 }}>{_iconForKind(n.kind || n.service)}</span>
                    {n.title || n.service_label || "Rendez-vous"}
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.7, lineHeight: 1.35 }}>
                    {n.merchant_name && <span>{n.merchant_name} · </span>}
                    {_fmtWhen(n.starts_at || n.datetime || n.scheduled_at)}
                  </div>
                </button>
              ))}
              <button
                data-testid="pad-halo-notif-view-all"
                onClick={() => {
                  setNotifOpen(false);
                  try { navigate("/mon-compte?tab=rdv"); }
                  catch (_) { window.location.href = "/mon-compte?tab=rdv"; }
                }}
                style={{
                  marginTop: 4,
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.14)",
                  borderRadius: 999,
                  padding: "8px 12px",
                  color: "#EBD9A8",
                  fontSize: 12, fontWeight: 700,
                  cursor: "pointer",
                }}
              >Voir tous mes rendez-vous →</button>
            </div>
          )}
        </div>
      )}

      {/* v3.6.1 — Panneau Kiosque (mini-cartes journaux, ex-rangée HomeCard). */}
      {newsOpen && (
        <div
          data-testid="pad-halo-news-panel"
          role="dialog"
          aria-label="Kiosque journaux"
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            right: 10,
            width: "min(420px, calc(100vw - 24px))",
            maxHeight: 380,
            overflowY: "auto",
            background: "#04180F",
            border: "1px solid rgba(16,185,129,0.30)",
            borderRadius: 14,
            boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
            zIndex: 60,
            padding: 12,
            color: "#F4F1E8",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 6 }}>
              <Newspaper size={13} strokeWidth={2.4} aria-hidden /> Kiosque du jour
            </div>
            <button
              data-testid="pad-halo-news-close"
              onClick={() => setNewsOpen(false)}
              aria-label="Fermer"
              style={{
                background: "transparent", border: "none", color: "inherit",
                cursor: "pointer", fontSize: 18, lineHeight: 1,
              }}
            >×</button>
          </div>
          <KiosqueStrip />
          <button
            data-testid="pad-halo-news-open-kiosque"
            onClick={() => {
              setNewsOpen(false);
              try { navigate("/media"); } catch (_) { window.location.href = "/media"; }
            }}
            style={{
              marginTop: 8,
              width: "100%",
              background: "transparent",
              border: "1px solid rgba(16,185,129,0.35)",
              borderRadius: 999,
              padding: "8px 12px",
              color: "#EBD9A8",
              fontSize: 12, fontWeight: 700,
              cursor: "pointer",
            }}
          >Ouvrir le Kiosque AYA →</button>
        </div>
      )}

      <CartDrawer
        open={cartOpen}
        onClose={() => { setCartOpen(false); fetchCart(); }}
        onGoToAccount={handleAccount}
      />

      {/* v3.0.0-rc.8w — AYA Live Discovery Drawer : liste des lives commerçants actifs. */}
      {liveOpen && (
        <LiveDiscoveryDrawer
          sessions={liveSessions}
          loading={liveLoading}
          onClose={() => setLiveOpen(false)}
          onRefresh={fetchLive}
        />
      )}

      <style>{`
        .aya-pad-halo-mask-btn:hover { transform: scale(1.06) rotate(-4deg); }
        .aya-pad-halo-mask-btn:active { transform: scale(0.94); }
        .aya-pad-halo-icon-btn:hover {
          transform: translateY(-1px);
          background: var(--civ-bg-hover, #f8fafc);
        }
        .aya-pad-halo-icon-btn:active { transform: scale(0.96); }
        .aya-cart-pulse-dot {
          animation: ayaCartPulse 1.6s ease-in-out infinite;
        }
        .aya-presence-pulse {
          animation: ayaPresencePulse 1.8s ease-in-out infinite;
        }
        @keyframes ayaCartPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.35); opacity: 0.75; }
        }
        @keyframes ayaPresencePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,126,27,0.55); }
          50%      { box-shadow: 0 0 0 6px rgba(255,126,27,0); }
        }
        html[data-theme="dark"] .aya-pad-halo-icon-btn {
          background: rgba(255,255,255,0.06);
          border-color: rgba(255,255,255,0.14);
          color: #f8fafc;
        }
        html[data-theme="dark"] .aya-pad-halo-icon-btn:hover {
          background: rgba(255,255,255,0.10);
        }
        /* v3.0.0-rc.8p — Halo mobile : marges latérales symétriques (10px) MAIS
           ras du bord bas (comme avant, aucune marge en dessous). */
        @media (max-width: 1023px) {
          .aya-livebanner-frame {
            position: fixed !important;
            bottom: env(safe-area-inset-bottom, 0px) !important;
            left: 10px !important;
            right: 10px !important;
            z-index: 50 !important;
            border-radius: 18px 18px 0 0 !important;
            padding: 2px 8px 6px 8px !important;
          }
          .aya-livebanner-frame .civ-input-bar {
            position: static !important;
            padding: 0 !important;
          }
          .aya-livebanner-frame .civ-input-shell {
            margin: 0 !important;
          }
        }

        /* v3.0.0-rc.8p — Effet "chenille lumineuse" qui parcourt le contour.
           On anime l'ANGLE du conic-gradient (@property --aya-halo-angle), pas
           l'élément lui-même — évite les débordements diagonaux hors du halo.
           L'arc lumineux (~70° = un bon 5e du périmètre) tourne à 5s. */
        @property --aya-halo-angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        .aya-livebanner-frame {
          position: relative;
        }
        .aya-livebanner-frame::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          padding: 1.5px;
          --aya-halo-angle: 0deg;
          /* v3.0.0-rc.8q — Base permanente ~0.22 (contour visible sur les 4 côtés,
             notamment le bas au-dessus de la barre système Android) + arc lumineux
             plus intense qui parcourt le tour comme une chenille. */
          background: conic-gradient(from var(--aya-halo-angle),
            rgba(255,255,255,0.22)  0deg,
            rgba(255,255,255,0.22)  260deg,
            rgba(235,217,168,0.65)  295deg,
            rgba(255,255,255,1)     325deg,
            rgba(235,217,168,0.65)  355deg,
            rgba(255,255,255,0.22)  380deg,
            rgba(255,255,255,0.22)  720deg);
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          animation: ayaHaloChenille 5s linear infinite;
          pointer-events: none;
          z-index: 0;
        }
        .aya-livebanner-frame > * { position: relative; z-index: 1; }
        @keyframes ayaHaloChenille {
          to { --aya-halo-angle: 360deg; }
        }
        /* v3.0.0-rc.8r — Attention pulse : quand AYA "pense" (streaming/tool call),
           la chenille passe de 5s ambient à 1.5s + arc plus lumineux. */
        .aya-livebanner-frame.aya-livebanner-thinking::before {
          animation-duration: 1.5s !important;
          background: conic-gradient(from var(--aya-halo-angle),
            rgba(255,255,255,0.28)  0deg,
            rgba(255,255,255,0.28)  240deg,
            rgba(255,220,150,0.85)  280deg,
            rgba(255,255,255,1)     320deg,
            rgba(255,220,150,0.85)  360deg,
            rgba(255,255,255,0.28)  400deg,
            rgba(255,255,255,0.28)  720deg) !important;
        }
        @media (prefers-reduced-motion: reduce) {
          .aya-livebanner-frame::before { animation: none; }
        }
      `}</style>
    </div>
  );
}


// v3.0.0-rc.8w — Drawer discovery des lives commerçants actifs.
// Modal centré bottom-sheet mobile / centré desktop, avec liste des sessions live
// enrichies avec produit épinglé, chiffre d'affaires temps réel, plateforme d'origine.
function LiveDiscoveryDrawer({ sessions, loading, onClose, onRefresh }) {
  const navigate = useNavigate();
  const platformLabel = (p) => ({ tiktok: "🎵 TikTok", instagram: "📸 Instagram", facebook: "📘 Facebook", snap: "👻 Snap", other: "🌐 Autre" }[p] || "🌐 Autre");
  const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");
  const goToLive = (s) => { onClose(); navigate(`/live/${s.merchant_id}?discover=1&session=${s.session_id}`); };
  return (
    <div
      role="dialog"
      aria-label="Découvrir les lives"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
        zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="live-discovery-drawer"
        style={{
          background: "#02120A", color: "#F4F1E8", width: "100%", maxWidth: 560,
          borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: "16px 16px 32px",
          maxHeight: "80vh", overflowY: "auto",
          border: "1px solid rgba(16,185,129,0.35)", boxShadow: "0 -8px 32px rgba(0,0,0,0.4)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.2 }}>🎥 Lives en cours</div>
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>Regarde et achète sans quitter AYA</div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button data-testid="live-discovery-refresh" onClick={onRefresh} style={{ padding: "6px 10px", borderRadius: 999, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", color: "#F4F1E8", fontSize: 12, cursor: "pointer" }}>↻</button>
            <button data-testid="live-discovery-close" onClick={onClose} style={{ padding: "6px 12px", borderRadius: 999, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", color: "#F4F1E8", fontSize: 12, cursor: "pointer" }}>×</button>
          </div>
        </div>
        {loading && sessions.length === 0 && (
          <div style={{ padding: "24px 8px", textAlign: "center", opacity: 0.6, fontSize: 13 }}>Chargement…</div>
        )}
        {!loading && sessions.length === 0 && (
          <div style={{ padding: "28px 12px", textAlign: "center", opacity: 0.65 }}>
            <div style={{ fontSize: 36, marginBottom: 6 }}>📡</div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Aucun live en direct pour l&apos;instant</div>
            <div style={{ fontSize: 11, marginTop: 4, opacity: 0.7 }}>Reviens plus tard, un commerçant partira bientôt en live 🎥</div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {sessions.map((s) => (
            <button
              key={s.session_id}
              data-testid={`live-discovery-card-${s.session_id.slice(0,8)}`}
              onClick={() => goToLive(s)}
              style={{
                textAlign: "left", cursor: "pointer",
                display: "flex", gap: 12, padding: 12,
                background: "linear-gradient(135deg, rgba(239,68,68,0.10), rgba(255,126,27,0.06))",
                border: "1px solid rgba(239,68,68,0.28)", borderRadius: 14, color: "inherit",
                position: "relative", overflow: "hidden",
              }}
            >
              {/* Vignette produit épinglé ou emoji placeholder */}
              <div style={{ width: 68, height: 68, borderRadius: 10, background: "#02120A", border: "1px solid rgba(232,213,163,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                {s.pinned_product?.photo ? (
                  <img src={s.pinned_product.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <span style={{ fontSize: 32 }}>{s.pinned_product?.emoji || "🛍"}</span>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 3 }}>
                  <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 999, background: "#EF4444", color: "#fff", letterSpacing: 0.5 }}>● EN DIRECT</span>
                  <span style={{ fontSize: 10, opacity: 0.75 }}>{platformLabel(s.platform_hint)}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.merchant_name || "Commerçant"}</div>
                {s.live_title && (
                  <div style={{ fontSize: 11.5, opacity: 0.75, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.live_title}</div>
                )}
                {s.pinned_product && (
                  <div style={{ fontSize: 11, marginTop: 4, color: "#EBD9A8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    📌 {s.pinned_product.name} · <strong>{fmt(s.pinned_product.price_fcfa)} FCFA</strong>
                  </div>
                )}
                <div style={{ display: "flex", gap: 10, marginTop: 6, fontSize: 10.5, opacity: 0.7 }}>
                  <span>👁 {fmt(s.viewers_count)}</span>
                  <span>🛒 {fmt(s.orders_count)}</span>
                  {s.gmv_fcfa > 0 && <span style={{ color: "#10B981", fontWeight: 700 }}>{fmt(s.gmv_fcfa)} F</span>}
                </div>
              </div>
              <div style={{ alignSelf: "center", fontSize: 22, opacity: 0.5 }}>›</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
